import { useEffect, useRef, useState } from 'react'
import { Check, Globe } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'
import { LOCALES, LOCALE_LABELS, isLocale, type Locale } from '@/i18n'
import ThemeToggle from './ThemeToggle'

/** A single, low-profile control for the two set-once preferences (language +
 *  light/dark). Both default from the browser, so this stays tucked behind one
 *  globe icon and opens a small popover only when needed. Replaces the separate
 *  header pills. */
export default function PreferencesMenu({
  className = '',
}: {
  className?: string
}) {
  const { i18n } = useTranslation()
  const current: Locale = isLocale(i18n.language) ? i18n.language : 'en'
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const switchTo = (target: Locale) => {
    const path = window.location.pathname
    const seg = path.split('/')[1]
    const stripped =
      isLocale(seg) && seg !== 'en' ? path.slice(seg.length + 1) || '/' : path
    const next =
      target === 'en'
        ? stripped
        : `/${target}${stripped === '/' ? '' : stripped}`
    document.cookie = `tla_lang=${target}; path=/; max-age=31536000; samesite=lax`
    window.location.assign(next + window.location.search)
  }

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Language and theme"
        title="Language & theme"
        className={cn(
          'grid place-items-center rounded-md p-1.5 transition-colors duration-300',
          open
            ? 'text-garage-cream'
            : 'text-garage-cream/40 hover:text-garage-cream/90'
        )}
      >
        <Globe className="h-4 w-4" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 min-w-[12rem] overflow-hidden rounded-xl border border-garage-accent/20 bg-garage-dark/95 shadow-garage-xl backdrop-blur-md"
        >
          <ul className="py-1">
            {LOCALES.map((l) => (
              <li key={l}>
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={l === current}
                  onClick={() => switchTo(l)}
                  className={cn(
                    'flex w-full items-center justify-between gap-3 px-4 py-2 text-sm transition-colors',
                    l === current
                      ? 'bg-garage-accent/10 text-garage-accent'
                      : 'text-garage-cream/80 hover:bg-garage-card/40 hover:text-garage-cream'
                  )}
                >
                  {LOCALE_LABELS[l]}
                  {l === current && <Check className="h-4 w-4 shrink-0" />}
                </button>
              </li>
            ))}
          </ul>
          <div className="border-t border-garage-accent/15 p-2">
            <ThemeToggle variant="row" />
          </div>
        </div>
      )}
    </div>
  )
}
